import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmdialogComponent } from '@shared/components/confirmdialog/confirmdialog.component';
import { FileuploadComponent } from '@shared/components/fileupload/fileupload.component';
import { SubMenuItem } from '@shared/components/header-sub-menu/header-sub-menu.component';
import { TransferErrorPopupComponent } from '@shared/components/transfer-error-popup/transfer-error-popup.component';
import { ProductionPlanTransferService } from '@shared/services/export-import-services/production-plan-config';
import { ImportValidatorService } from '@shared/services/filevalidators/Importvalidator.service';
import { NotificationService } from '@shared/services/notification.service';
import { Subject, takeUntil } from 'rxjs';
import { IConfiguration } from '../models/production-plan.models';
import { ProductionPlanConfigurationService } from '../production-plan-configuration.service';
import { CreateConfigurationComponent } from './create-configuration/create-configuration.component';
import { EditConfigurationComponent } from './edit-configuration/edit-configuration.component';

@Component({
	selector: 'app-production-plan-list',
	templateUrl: './list.component.html',
	styleUrls: ['./list.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class ProductionPlanListComponent implements OnDestroy, AfterViewInit {
	private destroy$ = new Subject<void>();
	displayedColumns: string[] = ['name', 'menu'];
	dataSource = new MatTableDataSource<IConfiguration>();
	isLoading = true;

	@ViewChild(MatPaginator) paginator: MatPaginator;
	@ViewChild(MatSort) sort: MatSort;

	favoriteItems: SubMenuItem[] = [
		{
			icon: () => 'acies_add',
			text: () => 'mes.production-plan.admin.create.button',
			click: () => this.openCreate(),
		},
	];

	menuItems: SubMenuItem[] = [
		{
			icon: () => 'acies_upload',
			text: () => 'mes.production-plan.admin.export.button',
			click: () => this.exportConfigurations(),
			// Export reads the plans and, to name what a status column refers to, the terminals.
			permissions: () => ['reporting:ProductionPlanGet', 'mes:TerminalGet'],
		},
		{
			icon: () => 'acies_download',
			text: () => 'mes.production-plan.admin.import.button',
			click: () => this.openImport(),
			// Import reads the same, and creates a plan that is new or updates one that exists.
			permissions: () => ['reporting:ProductionPlanGet', 'reporting:ProductionPlanCreate', 'reporting:ProductionPlanUpdate', 'mes:TerminalGet'],
		},
	];

	constructor(
		private dialog: MatDialog,
		private configurationService: ProductionPlanConfigurationService,
		private transferService: ProductionPlanTransferService,
		private importValidatorService: ImportValidatorService,
		private notificationService: NotificationService,
		private translator: TranslateService,
		private router: Router,
	) {}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}

	ngAfterViewInit() {
		this.dataSource.paginator = this.paginator;
		this.dataSource.sort = this.sort;
		this.dataSource.sortingDataAccessor = (item, property) => {
			if (property === 'name') return item.description?.toLowerCase() || '';
			return (item as any)[property];
		};
		this.updateList();
	}

	updateList() {
		this.isLoading = true;
		this.configurationService
			.get()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (configurations) => {
					this.dataSource.data = configurations;
					this.onSort(this.sort);
					this.isLoading = false;
				},
				error: () => {
					this.isLoading = false;
					this.notificationService.notify(['mes.production-plan.admin.notifications.load-error.header'], ['mes.production-plan.admin.notifications.load-error.content'], 'error');
				},
			});
	}

	applyFilter(event: KeyboardEvent) {
		if (event.target instanceof HTMLInputElement) {
			const filterValue = event.target.value;
			this.dataSource.filter = filterValue.trim().toLowerCase();
		}
	}

	onSort(sort: Sort) {
		if (sort.direction === '') {
			this.dataSource.data = [...this.dataSource.data].sort((a, b) => a.alias.localeCompare(b.alias));
		}
	}

	exportConfigurations(): void {
		this.transferService
			.exportData(this.dataSource.data)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: () => {
					this.notificationService.notify(['mes.production-plan.admin.export.notification.success.header'], ['mes.production-plan.admin.export.notification.success.content'], 'success');
				},
				error: (error: Error) => {
					console.error('Failed to export the production plans', error);
					this.notificationService.notify(['mes.production-plan.admin.export.notification.error.header'], ['mes.production-plan.admin.export.notification.error.content'], 'error');
					this.dialog.open(TransferErrorPopupComponent, {
						width: '450px',
						data: { message: error.message },
					});
				},
			});
	}

	openImport(): void {
		const dialogRef = this.dialog.open(FileuploadComponent, {
			width: '450px',
			data: {
				headingKey: 'mes.production-plan.admin.import.title',
				subHeadingKey: 'mes.production-plan.admin.import.subtitle',
				confirmButtonKey: 'mes.production-plan.admin.import.button',
				notificationHeader: 'mes.production-plan.admin.import.notification.success.header',
				notificationContent: 'mes.production-plan.admin.import.notification.success.content',
				notificationErrorHeader: 'mes.production-plan.admin.import.notification.error.header',
				notificationErrorContent: 'mes.production-plan.admin.import.notification.error.content',
				notificationClientErrorHeader: 'mes.production-plan.admin.import.notification.clientError.header',
				notificationClientErrorContent: 'mes.production-plan.admin.import.notification.clientError.content',
				detailedClientErrorMessage: 'fileuploader.detailed-client-error-message',
				allowedFileTypes: ['.json'],
				validatorService: this.importValidatorService,
				data: this.transferService,
			},
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy$))
			.subscribe(() => this.updateList());
	}

	openCreate(): void {
		const dialogRef = this.dialog.open(CreateConfigurationComponent, {
			disableClose: true,
			width: '950px',
		});
		dialogRef.componentInstance.created.pipe(takeUntil(this.destroy$)).subscribe((created: IConfiguration) => {
			if (created) {
				this.dataSource.data = [...this.dataSource.data, created];
				this.onSort(this.sort);
			}
		});
	}

	openEdit(configuration: IConfiguration): void {
		const dialogRef = this.dialog.open(EditConfigurationComponent, {
			disableClose: true,
			width: '950px',
			data: configuration,
		});
		dialogRef.componentInstance.updated.pipe(takeUntil(this.destroy$)).subscribe((updated: IConfiguration) => {
			if (updated) {
				const index = this.dataSource.data.findIndex((c) => c.id === updated.id);
				if (index !== -1) {
					this.dataSource.data[index] = updated;
					this.dataSource.data = [...this.dataSource.data];
				}
				this.onSort(this.sort);
			}
		});
	}

	onDelete(configuration: IConfiguration) {
		const dialogRef = this.dialog.open(ConfirmdialogComponent, {
			width: '450px',
			data: {
				okReturnData: true,
				cancelReturnData: false,
				heading: this.translator.instant('mes.production-plan.admin.delete.heading'),
				content: this.translator.instant('mes.production-plan.admin.delete.content', {
					alias: configuration.alias,
				}),
				okButtonText: this.translator.instant('general.ok'),
				cancelButtonText: this.translator.instant('general.cancel'),
			},
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy$))
			.subscribe((result) => {
				if (result) {
					this.configurationService
						.delete(configuration.id)
						.pipe(takeUntil(this.destroy$))
						.subscribe({
							next: () => {
								this.notificationService.notify(['mes.production-plan.admin.notifications.delete.header'], ['mes.production-plan.admin.notifications.delete.content', { alias: configuration.alias }], 'success');
								this.dataSource.data = this.dataSource.data.filter((c) => c.id !== configuration.id);
							},
							error: () => {
								this.notificationService.notify(['mes.production-plan.admin.notifications.delete-error.header'], ['mes.production-plan.admin.notifications.delete-error.content'], 'error');
							},
						});
				}
			});
	}

	openTerminal(configuration: IConfiguration | null) {
		void this.router.navigate([`/mes/production-plans/${configuration.id}`]);
	}
}
